import { Injectable, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ToastController } from '@ionic/angular/standalone';
import { isSafeDeepLink } from './push.service';
import type { PushPayload } from './push.types';
import { AuthStore } from '../auth/auth.store';
import { normalizePushRoute } from '../deep-link.service';

/**
 * Phase 15 (PUSH-03 — D-Discretion) — foreground push banner.
 *
 * When a push arrives while the app is OPEN (Capacitor/web onMessage), iOS
 * still suppresses the OS notification (controlled by capacitor.config
 * PushNotifications.presentationOptions) and Android shows the system banner.
 * On both, we display an Ionic Toast at the top with a "Ver" action that deep-
 * links via the Angular Router — staying on-screen for 5 s.
 *
 * Threat T-15-10: deep-link from push payload is filtered through
 * isSafeDeepLink — external URLs / protocol-relative paths get the button
 * hidden so a malicious payload cannot navigate the user off-app.
 */
@Injectable({ providedIn: 'root' })
export class ForegroundBannerService {
  private readonly toastCtrl = inject(ToastController);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly authStore = inject(AuthStore);

  async show(payload: PushPayload): Promise<void> {
    const deepLinkCandidate = payload.data?.deepLink;
    const safeDeepLink = isSafeDeepLink(deepLinkCandidate)
      ? normalizePushRoute(deepLinkCandidate, this.authStore.user()?.role)
      : null;

    const message = payload.body
      ? `${payload.title}: ${payload.body}`
      : payload.title || 'Nova notificação';

    const toast = await this.toastCtrl.create({
      message,
      duration: 5000,
      position: 'top',
      color: 'primary',
      buttons: safeDeepLink
        ? [
            {
              text: 'Ver',
              handler: () => {
                this.zone.run(() => {
                  void this.router.navigateByUrl(safeDeepLink);
                });
              },
            },
          ]
        : [],
    });
    await toast.present();
  }
}
